import { createAction, createSlice } from "@reduxjs/toolkit";

import listFactory from "../../../store/listFactory";
import {
  isClearJunction,
  isIFXPayments,
} from "../helpers/ProviderCredentialGuards";

import type { RootState } from "../../../store";
import type { UUID } from "../../../utils";
import type {
  CashManagementProviderCustomerConfig,
  CashManagementProviderCustomerConfigCredentials,
} from "../models/ProviderCustomerConfig";

export interface ProviderCustomerConfigUUID {
  customerId?: UUID;
  providerId?: UUID;
  configId?: UUID;
}

const {
  slicePart,
  selectors,
} = listFactory<CashManagementProviderCustomerConfig>(
  "cashManagementProviderCustomerConfig"
);

const initialState = {
  ...slicePart.initialState,
};

export const slice = createSlice({
  ...slicePart,
  initialState,
  reducers: {
    setItem: slicePart.reducers.setItem,
    setList: slicePart.reducers.setList,
  },
});

export const {
  setItem: setProviderCustomerConfig,
  setList: setProviderCustomerConfigs,
} = slice.actions;

export const createProviderCustomerConfig = createAction<{
  customerId: UUID;
  providerId: UUID;
  data: CashManagementProviderCustomerConfigCredentials;
}>("createProviderCustomerConfig");

export const fetchProviderCustomerConfigs = createAction<{
  customerId: UUID;
}>("fetchProviderCustomerConfigs");

export const fetchProviderCustomerConfig = createAction<{
  configId: UUID;
}>("fetchProviderCustomerConfig");

export const updateProviderCustomerConfig = createAction<{
  configId: UUID;
  providerId: UUID;
  data: CashManagementProviderCustomerConfigCredentials;
}>("updateProviderCustomerConfig");

export const selectProviderCustomerConfigs = (customerId: UUID) => (
  state: RootState
) => {
  return state.cashManagementProviderCustomerConfig?.list?.filter(
    (config) => config.customerId === customerId
  );
};

export const { selectItem: selectProviderCustomerConfig } = selectors;

export const selectProviderHasOAuthConfigured = ({
  customerId,
  providerId,
}: {
  customerId: UUID;
  providerId: UUID;
}) => (state: RootState) => {
  let selectedProvider = selectProviderCustomerConfig({
    customerId,
    cashManagementProvider: {
      id: providerId,
    },
  })(state);

  if (selectedProvider && isIFXPayments(selectedProvider)) {
    return !!(
      selectedProvider.credentials.clientId &&
      selectedProvider.credentials.clientSecret &&
      selectedProvider.credentials.password &&
      selectedProvider.credentials.username
    );
  } else if (selectedProvider && isClearJunction(selectedProvider)) {
    return true;
  } else {
    return false;
  }
};

export const providerCustomerConfigOperationPaths = (
  ids: ProviderCustomerConfigUUID
) => {
  const { customerId, configId } = ids;

  return {
    listing: [
      "cashManagement",
      "providerCustomerConfig",
      "listing",
      customerId,
    ],
    details: ["cashManagement", "providerCustomerConfig", "details", configId],
    update: ["cashManagement", "providerCustomerConfig", "update", configId],
    create: ["cashManagement", "providerCustomerConfig", "providers", configId],
  };
};

export default slice.reducer;
